// src/utils/api.js
import { getFirebaseToken } from '../authService';

/**
 * Función helper para hacer requests autenticadas a REACT_APP_API_URL
 * Agrega automáticamente el token de Firebase en el header Authorization
 * 
 * @param {string} endpoint - El endpoint relativo (ej: '/datos-usuario')
 * @param {RequestInit} options - Opciones de fetch (method, body, headers, etc.)
 * @param {boolean} forceRefresh - Si es true, fuerza la renovación del token
 * @returns {Promise<Response>} - La respuesta de fetch
 */
export async function apiRequest(endpoint, options = {}, forceRefresh = false) {
  const baseUrl = process.env.REACT_APP_API_URL;
  
  if (!baseUrl) {
    console.error('❌ [apiRequest] REACT_APP_API_URL no está configurado');
    throw new Error('REACT_APP_API_URL no está configurado');
  }

  // Asegurar que el endpoint empiece con /
  const url = endpoint.startsWith('/') 
    ? `${baseUrl}${endpoint}` 
    : `${baseUrl}/${endpoint}`;
  

  // Obtener el token de Firebase
  let token = await getFirebaseToken(forceRefresh);
  
  if (!token) {
    console.error('❌ [apiRequest] No se pudo obtener el token de Firebase');
    throw new Error('No se pudo obtener el token de autenticación. Por favor, inicia sesión nuevamente.');
  }
  

  // Preparar headers (FormData: el browser define Content-Type con boundary)
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };
  if (isFormData) {
    delete headers['Content-Type'];
  }

  // Preparar opciones de fetch
  const fetchOptions = {
    ...options,
    headers,
  };



  try {
    const startTime = Date.now();
    
    const response = await fetch(url, fetchOptions);
    
    const duration = Date.now() - startTime;

    // Si el token expiró (401), intentar renovarlo y reintentar una vez
    if (response.status === 401 && !forceRefresh) {
      const refreshedToken = await getFirebaseToken(true);
      
      
      if (refreshedToken && refreshedToken !== token) {
        // Actualizar el header con el token renovado
        headers['Authorization'] = `Bearer ${refreshedToken}`;
        
        const retryStartTime = Date.now();
        const retryResponse = await fetch(url, {
          ...fetchOptions,
          headers,
        });
        const retryDuration = Date.now() - retryStartTime;
        
        if (retryResponse.ok) {
          return retryResponse;
        } else {
          console.error('❌ [apiRequest] Reintento falló con status:', retryResponse.status);
        }
      } else {
        console.error('❌ [apiRequest] No se pudo renovar el token o el token no cambió');
      }
      
      // Si después de renovar sigue fallando, lanzar error
      console.error('❌ [apiRequest] Token inválido después de renovar');
      throw new Error('La sesión expiró. Por favor, inicia sesión nuevamente.');
    }

    // Log de errores de respuesta
    if (!response.ok) {
      console.error('❌ [apiRequest] Respuesta con error:', {
        status: response.status,
        statusText: response.statusText,
        url: url,
      });
      
      // Intentar leer el body del error
      try {
        const errorText = await response.text();
        console.error('❌ [apiRequest] Body del error:', errorText.substring(0, 500));
      } catch (e) {
        console.error('❌ [apiRequest] No se pudo leer el body del error:', e);
      }
    }

    return response;
  } catch (error) {
    console.error('❌ [apiRequest] ===== ERROR EN REQUEST =====');
    console.error('❌ [apiRequest] Tipo de error:', error.constructor.name);
    console.error('❌ [apiRequest] Mensaje:', error.message);
    console.error('❌ [apiRequest] Stack:', error.stack);
    console.error('❌ [apiRequest] URL que falló:', url);
    console.error('❌ [apiRequest] Options que fallaron:', {
      method: options.method || 'GET',
      hasBody: !!options.body,
    });
    console.error('❌ [apiRequest] ===== FIN DE ERROR =====');
    throw error;
  }
}

/**
 * Helper para hacer GET requests
 */
export async function apiGet(endpoint, options = {}) {
  try {
    const response = await apiRequest(endpoint, { ...options, method: 'GET' });
    return response;
  } catch (error) {
    console.error('❌ [apiGet] Error en GET request:', error);
    throw error;
  }
}

/**
 * Helper para hacer POST requests
 */
export async function apiPost(endpoint, data, options = {}) {
  try {
    const response = await apiRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error('❌ [apiPost] Error en POST request:', error);
    throw error;
  }
}

/**
 * Helper para hacer PUT requests
 */
export async function apiPut(endpoint, data, options = {}) {
  try {
    const response = await apiRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error('❌ [apiPut] Error en PUT request:', error);
    throw error;
  }
}

/**
 * Helper para hacer DELETE requests
 */
export async function apiDelete(endpoint, options = {}) {
  try {
    const response = await apiRequest(endpoint, { ...options, method: 'DELETE' });
    return response;
  } catch (error) {
    console.error('❌ [apiDelete] Error en DELETE request:', error);
    throw error;
  }
}

/**
 * Helper para hacer PATCH requests
 */
export async function apiPatch(endpoint, data, options = {}) {
  try {
    const response = await apiRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response;
  } catch (error) {
    console.error('❌ [apiPatch] Error en PATCH request:', error);
    throw error;
  }
}

